// Visual-only world spacing. Do not use these values for snapping or document
// geometry. The three layers and their fades mirror CodeCanvas's tuned grid.
import {
  CANVAS_BLOCK_SIZE as BLOCK_GAP,
  CANVAS_CELL_SIZE as CELL_GAP,
  CANVAS_SECTOR_SIZE as SECTOR_GAP,
} from '@blocksuite/store';
const CELL_FADE_START_PX = 14;
const CELL_FADE_END_PX = 28;
const BLOCK_FADE_START_PX = 8;
const BLOCK_FADE_END_PX = 20;
const SECTOR_FADE_START_PX = 11;
const SECTOR_FADE_END_PX = 40;
const DETAIL_REVEAL_START_ZOOM = 0.08;
const DETAIL_REVEAL_END_ZOOM = 1 / 9;
const SECTOR_DIP_START_PX = 40;
const SECTOR_DIP_CENTER_PX = 120;
const SECTOR_DIP_END_PX = 240;
const SECTOR_MINIMUM_ALPHA = 0.84;
const SECTOR_OVERVIEW_ALPHA_FLOOR = smoothstep(
  SECTOR_FADE_START_PX,
  SECTOR_FADE_END_PX,
  SECTOR_GAP * 0.03
);

function smoothstep(start: number, end: number, value: number) {
  const progress = Math.min(1, Math.max(0, (value - start) / (end - start)));
  return progress * progress * (3 - 2 * progress);
}

function sectorHierarchyOpacity(screenGap: number) {
  if (screenGap <= SECTOR_DIP_START_PX || screenGap >= SECTOR_DIP_END_PX) {
    return 1;
  }
  const progress =
    screenGap <= SECTOR_DIP_CENTER_PX
      ? smoothstep(SECTOR_DIP_START_PX, SECTOR_DIP_CENTER_PX, screenGap)
      : 1 - smoothstep(SECTOR_DIP_CENTER_PX, SECTOR_DIP_END_PX, screenGap);
  return 1 - progress * (1 - SECTOR_MINIMUM_ALPHA);
}

/** Ordered back to front; widths stay in CSS pixels regardless of canvas zoom. */
export function planEdgelessLineGrid(zoom: number) {
  const scale = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const detailOpacity = smoothstep(
    DETAIL_REVEAL_START_ZOOM,
    DETAIL_REVEAL_END_ZOOM,
    scale
  );
  return [
    {
      level: 'cell',
      worldGap: CELL_GAP,
      screenGap: CELL_GAP * scale,
      lineWidth: 0.5,
      opacity:
        smoothstep(CELL_FADE_START_PX, CELL_FADE_END_PX, CELL_GAP * scale) *
        detailOpacity,
    },
    {
      level: 'block',
      worldGap: BLOCK_GAP,
      screenGap: BLOCK_GAP * scale,
      lineWidth: 0.75,
      opacity:
        smoothstep(BLOCK_FADE_START_PX, BLOCK_FADE_END_PX, BLOCK_GAP * scale) *
        detailOpacity,
    },
    {
      level: 'sector',
      worldGap: SECTOR_GAP,
      screenGap: SECTOR_GAP * scale,
      lineWidth: 1,
      opacity:
        Math.max(
          SECTOR_OVERVIEW_ALPHA_FLOOR,
          smoothstep(
            SECTOR_FADE_START_PX,
            SECTOR_FADE_END_PX,
            SECTOR_GAP * scale
          )
        ) * sectorHierarchyOpacity(SECTOR_GAP * scale),
    },
  ] as const;
}
